import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import type { Application } from '../../../types/application';
import type { AlertLog } from '../types';
import { biztalkTemplates } from '../../../../../../verify-functions/src/utils/biztalkTemplates';

interface Props {
  open: boolean;
  application: Application;
  log: AlertLog | null;
  onClose: () => void;
}

// 📱 전화번호 마스킹
function maskPhone(phone?: string) {
  if (!phone) return '-';
  if (phone.length < 7) return phone;
  return phone.slice(0, 3) + '****' + phone.slice(-4);
}

// 🧩 버튼 URL 치환
function resolveUrl(url: string, applicationId?: string, deviceId?: string) {
  return url
    .replace('#{application_id}', applicationId || '')
    .replace('#{device_id}', deviceId || '');
}

// 💬 템플릿 기반 메시지 재구성
function buildMessage(params: {
  event?: string;
  application: Application;
  log: AlertLog | null;
}) {
  const { event, application, log } = params;
  if (!event) return '';

  const template = biztalkTemplates[event];
  if (!template) return '';

  const applyDate = application.created_at
    ? new Date((application.created_at as any).seconds * 1000).toLocaleDateString('ko-KR')
    : new Date().toLocaleDateString('ko-KR');

  // 디바이스 기준 정보(차감 사유 추출 시 사용)
  const matchedDevice = log?.device_id
    ? (application.devices || []).find(d => d.id === log.device_id)
    : undefined;

  const priceStr = (log?.price != null ? Number(log.price) : NaN);
  const formattedPrice = isNaN(priceStr) ? undefined : priceStr.toLocaleString();

  const reason = matchedDevice?.deduction_reason || undefined;

  try {
    return template.getMessage({
      userName: log?.user_name || application.user_name,
      deviceName: log?.device_name || matchedDevice?.deviceModel,
      applyDate,
      price: formattedPrice,
      reason,
      application_id: application.id,
      device_id: log?.device_id,
      returnTrackingNumber: (application as any)?.return_tracking_number,
    });
  } catch (e) {
    return '';
  }
}

export const AlertHistoryDetailDialog: React.FC<Props> = ({ open, application, log, onClose }) => {
  // 재구성 메시지 및 템플릿/버튼 정보 메모이제이션
  const { message, templateCode, buttons } = useMemo(() => {
    const event = log?.event;
    const template = event ? biztalkTemplates[event] : undefined;
    const rebuilt = buildMessage({ event, application, log });
    return {
      message: rebuilt,
      templateCode: template?.tmpltCode,
      buttons: template?.buttons || [],
    };
  }, [application, log]);

  const rawLogPretty = useMemo(() => {
    if (!log) return '';
    try {
      return JSON.stringify(log, null, 2);
    } catch {
      return '';
    }
  }, [log]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // noop
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.2px' }}>
              알림톡 전송 상세
            </Typography>
            {log && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  이벤트: {log.event}
                </Typography>
                <Chip
                  label={log.status === 'success' ? '성공' : '실패'}
                  size="small"
                  sx={{ ml: 1, bgcolor: log.status === 'success' ? '#4caf50' : '#f44336', color: '#fff', height: 18 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#fafbfd' }}>
        {log && (
          <Stack spacing={2}>
            {/* 상단 요약 카드 */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e1e8ed', background: 'linear-gradient(180deg, #ffffff 0%, #f7faff 100%)' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">템플릿 코드</Typography>
                  <Typography variant="body2" fontWeight={600}>{templateCode || '-'}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">디바이스</Typography>
                  <Typography variant="body2" fontWeight={600}>{log.device_name || '-'}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">가격</Typography>
                  <Typography variant="body2" fontWeight={600}>{log.price != null ? Number(log.price).toLocaleString() + '원' : '-'}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">전화번호</Typography>
                  <Typography variant="body2" fontWeight={600}>{maskPhone(log.phone_number)}</Typography>
                </Box>
              </Stack>
            </Paper>

            {/* 재구성된 메시지 */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>재구성된 메시지</Typography>
                <Tooltip title="메시지 복사">
                  <IconButton size="small" onClick={() => handleCopy(message)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e1e8ed' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#1565c0', lineHeight: 1.6 }}>
                  {message || '템플릿 또는 파라미터 부족으로 메시지를 재구성할 수 없습니다.'}
                </Typography>
              </Paper>
            </Box>

            {/* 버튼/링크 미리보기 */}
            {buttons && buttons.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>버튼/링크</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {buttons.map((btn, idx) => (
                    <Paper key={idx} elevation={0} sx={{ p: 1, px: 1.5, borderRadius: 999, border: '1px solid #e1e8ed', bgcolor: '#fff' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={btn.type} size="small" sx={{ height: 20 }} />
                        <Typography variant="body2" fontWeight={600}>{btn.name}</Typography>
                        {btn.type === 'WL' && (
                          <Tooltip title="링크 비활성화됨">
                            <span>
                              <IconButton size="small" disabled>
                                <OpenInNewIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* 원시 로그 */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>원시 로그(JSON)</Typography>
                <Tooltip title="JSON 복사">
                  <IconButton size="small" onClick={() => handleCopy(rawLogPretty)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#0b1020', color: '#e3f2fd', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12, border: '1px solid #1b233b' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{rawLogPretty}</pre>
              </Paper>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertHistoryDetailDialog;


